<template>
  <div class="credit-card-payment">
    <div class="container">
      <div class="payment-content">
        <!-- Credit Card Preview -->
        <div class="card-preview-container">
          <div class="credit-card" :class="{ flipped: isCardFlipped }">
            <!-- Front of card -->
            <div class="card-front">
              <div class="card-header">
                <div class="card-chip"></div>
                <div class="card-brand">
                  <img
                    v-if="cardBrand"
                    :src="getCardBrandIcon(cardBrand)"
                    :alt="cardBrand"
                    class="brand-icon"
                  />
                </div>
              </div>
              <div class="card-number">
                {{ formatCardNumber(cardNumber) }}
              </div>
              <div class="card-details">
                <div class="card-holder">
                  <span class="label">{{ t('Card Holder') }}</span>
                  <span class="value">{{ cardHolder || t('FULL NAME') }}</span>
                </div>
                <div class="card-expiry">
                  <span class="label">{{ t('Expires') }}</span>
                  <span class="value">{{ formatExpiry(expiryDate) || 'MM/YY' }}</span>
                </div>
              </div>
            </div>

            <!-- Back of card -->
            <div class="card-back">
              <div class="card-stripe"></div>
              <div class="card-signature">
                <span class="signature-label">{{ t('Authorized Signature') }}</span>
                <div class="signature-line"></div>
              </div>
              <div class="card-cvv">
                <span class="cvv-label">{{ t('CVV') }}</span>
                <span class="cvv-value">{{ '***' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Payment Form -->
        <div class="payment-form">
          <validation-observer ref="paymentForm" v-slot="{ handleSubmit }">
            <b-form @submit.prevent="handleSubmit(processPayment)">
              <b-row>
                <!-- Card Number -->
                <b-col cols="12">
                  <b-form-group :label="t('Card Number')" label-for="card-number">
                    <validation-provider
                      #default="{ errors }"
                      :name="t('Card Number')"
                      rules="required"
                    >
                      <b-form-input
                        id="card-number"
                        v-model="cardNumber"
                        :placeholder="t('1234 5678 9012 3456')"
                        maxlength="19"
                        @input="formatCardNumberInput"
                        @focus="isCardFlipped = false"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <!-- Card Holder Name -->
                <b-col cols="12">
                  <b-form-group :label="t('Card Holder Name')" label-for="card-holder">
                    <validation-provider
                      #default="{ errors }"
                      :name="t('Card Holder Name')"
                      rules="required|alpha_spaces"
                    >
                      <b-form-input
                        id="card-holder"
                        v-model="cardHolder"
                        :placeholder="t('John Doe')"
                        @focus="isCardFlipped = false"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <!-- Expiry Date and CVV -->
                <b-col sm="6">
                  <b-form-group :label="t('Expiry Date')" label-for="expiry-date">
                    <validation-provider
                      #default="{ errors }"
                      :name="t('Expiry Date')"
                      rules="required"
                    >
                      <b-form-input
                        id="expiry-date"
                        v-model="expiryDate"
                        :placeholder="t('MM/YY')"
                        maxlength="5"
                        @input="formatExpiryInput"
                        @focus="isCardFlipped = false"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col sm="6">
                  <b-form-group :label="t('CVV')" label-for="cvv">
                    <validation-provider #default="{ errors }" :name="t('CVV')" rules="required">
                      <b-form-input
                        id="cvv"
                        v-model="cvv"
                        :placeholder="t('123')"
                        maxlength="4"
                        type="password"
                        @focus="isCardFlipped = true"
                        @blur="isCardFlipped = false"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <!-- Amount -->
                <b-col cols="12">
                  <b-form-group :label="t('Amount')" label-for="amount">
                    <validation-provider #default="{ errors }" :name="t('Amount')">
                      <b-input-group>
                        <b-input-group-prepend>
                          <b-input-group-text>{{ currency }}</b-input-group-text>
                        </b-input-group-prepend>
                        <b-form-input
                          disabled
                          id="amount"
                          v-model="amount"
                          :placeholder="t('0.00')"
                          type="number"
                          step="0.01"
                          min="0"
                        />
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <!-- Submit Button -->
                <b-col cols="12">
                  <div class="payment-actions">
                    <b-button
                      type="submit"
                      variant="primary"
                      size="lg"
                      :disabled="submitting"
                      class="payment-button"
                    >
                      <b-spinner v-if="submitting" small class="mr-2"></b-spinner>
                      {{ submitting ? t('Processing...') : t('Pay Now') }}
                    </b-button>
                  </div>
                </b-col>
              </b-row>
            </b-form>
          </validation-observer>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BButton,
  BSpinner,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupText
} from 'bootstrap-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper';
import useService from '@/auth/service/useService';

export default {
  name: 'PayWithCreditCard',
  components: {
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BButton,
    BSpinner,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupText,
    ValidationObserver,
    ValidationProvider
  },
  mixins: [helper],
  data() {
    return {
      cardNumber: '',
      cardHolder: '',
      expiryDate: '',
      cvv: '',
      amount: this.$route.query.amount,
      currency: this.$route.query.currency,
      isCardFlipped: false,
      submitting: false
    };
  },
  computed: {
    cardBrand() {
      const number = this.cardNumber.replace(/\s/g, '');

      if (number.startsWith('4')) return 'visa';
      if (number.startsWith('5')) return 'mastercard';

      return null;
    }
  },
  methods: {
    redirectToSecurePayment(htmlContent) {
      document.open();
      document.write(htmlContent);
      document.close();
    },

    formatCardNumberInput(value) {
      const cleaned = value.replace(/\D/g, '');

      const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');

      this.cardNumber = formatted;
    },

    formatCardNumber(number) {
      if (!number) return '•••• •••• •••• ••••';

      const cleaned = number.replace(/\s/g, '');
      const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');

      const masked = formatted.replace(/\d(?=\d{4})/g, '•');

      return masked;
    },

    formatExpiryInput(value) {
      const cleaned = value.replace(/\D/g, '');

      if (cleaned.length >= 2) {
        const month = cleaned.substring(0, 2);
        const year = cleaned.substring(2, 4);
        this.expiryDate = `${month}/${year}`;
      } else {
        this.expiryDate = cleaned;
      }
    },

    formatExpiry(expiry) {
      if (!expiry) return '';
      return expiry;
    },

    getCardBrandIcon(brand) {
      const icons = {
        visa: '/assets/images/icons/visa.svg',
        mastercard: '/assets/images/icons/mastercard.svg'
      };
      return icons[brand] || '';
    },

    async processPayment() {
      try {
        this.submitting = true;

        const paymentData = {
          paymentIntentId: this.$route.params.paymentIntentId,
          card_number: this.cardNumber.replace(/\s/g, ''),
          card_holder: this.cardHolder,
          expiry_month: this.expiryDate.split('/')[0],
          expiry_year: this.expiryDate.split('/')[1],
          cvv: this.cvv
        };

        useService.paymentService
          .payWithCreditCard(paymentData)
          .then(response => {
            if (response.status) {
              this.redirectToSecurePayment(response.data.html_content);
            } else {
              throw new Error(
                response.data.message || this.$t('Payment failed. Please try again.')
              );
            }
          })
          .catch(error => {
            const errorMessage = Array.isArray(error)
              ? error?.[0]?.message
              : error?.message || this.$t('Payment failed. Please try again.');
            this.showErrorMessage(errorMessage);
          })
          .finally(() => {
            this.submitting = false;
          });
      } catch (error) {
        this.showErrorMessage(this.$t('Payment failed. Please try again.'));
      }
    },

    resetForm() {
      this.cardNumber = '';
      this.cardHolder = '';
      this.expiryDate = '';
      this.cvv = '';
      this.amount = '';
      this.isCardFlipped = false;

      if (this.$refs.paymentForm) {
        this.$refs.paymentForm.reset();
      }
    }
  },
  setup() {
    const { t } = useI18nUtils();
    return { t };
  }
};
</script>

<style scoped lang="scss">
// Variables
$primary-gradient: linear-gradient(135deg, #05a6ce 0%, #0389b3 100%);
$gold-gradient: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
$card-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
$form-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
$border-radius: 15px;
$transition-duration: 0.6s;

// Mixins
@mixin flex-center {
  display: flex;
  justify-content: center;
  align-items: center;
}

@mixin flex-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

@mixin card-side {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: $border-radius;
  padding: 1.5rem;
  box-shadow: $card-shadow;
}

@mixin responsive-breakpoint($breakpoint) {
  @media (max-width: $breakpoint) {
    @content;
  }
}

.credit-card-payment {
  min-height: 100vh;
  padding: 2rem 0;
}

.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1rem;
}

.payment-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.payment-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
}

.payment-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: start;
}

.card-preview-container {
  position: sticky;
  top: 2rem;
}

.credit-card {
  width: 100%;
  max-width: 400px;
  height: 250px;
  position: relative;
  transform-style: preserve-3d;
  transition: transform $transition-duration;
  margin: 0 auto;

  &.flipped {
    transform: rotateY(180deg);
  }
}

.card-front,
.card-back {
  @include card-side;
  background: $primary-gradient;
  color: white;
}

.card-back {
  transform: rotateY(180deg);
}

.card-header {
  @include flex-between;
  margin-bottom: 2rem;
}

.card-chip {
  width: 50px;
  height: 40px;
  background: $gold-gradient;
  border-radius: 8px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 30px;
    height: 20px;
    background: repeating-linear-gradient(
      90deg,
      transparent,
      transparent 2px,
      rgba(0, 0, 0, 0.1) 2px,
      rgba(0, 0, 0, 0.1) 4px
    );
  }
}

.brand-icon {
  height: 40px;
  width: auto;
}

.card-number {
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: 2px;
  margin-bottom: 2rem;
  font-family: 'Courier New', monospace;
}

.card-details {
  @include flex-between;
  align-items: flex-end;
}

.card-holder,
.card-expiry {
  display: flex;
  flex-direction: column;
}

.label {
  font-size: 0.8rem;
  opacity: 0.8;
  margin-bottom: 0.25rem;
}

.value {
  font-size: 1rem;
  font-weight: 600;
}

.card-stripe {
  height: 50px;
  background: #333;
  margin-bottom: 1rem;
}

.card-signature {
  margin-bottom: 1rem;
}

.signature-label {
  font-size: 0.8rem;
  opacity: 0.8;
  display: block;
  margin-bottom: 0.5rem;
}

.signature-line {
  height: 30px;
  background: white;
  border-radius: 4px;
}

.card-cvv {
  @include flex-between;
}

.cvv-label {
  font-size: 0.8rem;
  opacity: 0.8;
}

.cvv-value {
  font-size: 1rem;
  font-weight: 600;
  background: white;
  color: #333;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  min-width: 40px;
  text-align: center;
}

.payment-form {
  background: white;
  padding: 2rem;
  border-radius: $border-radius;
  box-shadow: $form-shadow;

  body.dark-layout & {
    background: #3f4a6b;
  }
}

.payment-actions {
  margin-top: 2rem;
  text-align: center;
}

.payment-button {
  min-width: 200px;
  height: 50px;
  font-weight: 600;
  border-radius: 25px;
  background: $primary-gradient;
  border: none;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(5, 166, 206, 0.4);
  }

  &:disabled {
    transform: none;
    box-shadow: none;
  }
}

@include responsive-breakpoint(768px) {
  .payment-content {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .card-preview-container {
    position: static;
  }

  .credit-card {
    max-width: 350px;
    height: 250px;
  }

  .payment-title {
    font-size: 2rem;
  }

  .payment-form {
    padding: 1.5rem;
  }
}

@include responsive-breakpoint(480px) {
  .credit-card {
    max-width: 300px;
    height: 200px;
  }

  .card-number {
    font-size: 1.2rem;
  }

  .payment-form {
    padding: 1rem;
  }
}
</style>
